import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import axios from "axios";
import { useSnackbar } from "notistack";
import { SLEDGEHAMMER_BASE_API } from "../config";
import AppShell from "../components/AppShell";
import { downloadPtsTemplate } from "../utils/ptsTemplate";
import containersShip from "../assets/containers-ship-v4.gif";

const APPLY_BATCH_SIZE = 25;

const PtsUpdates = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [file, setFile] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [uploaded, setUploaded] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);

  const pageRows = useMemo(() => {
    const rows = result?.rows ?? [];
    return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [result, page, rowsPerPage]);

  const checkFile = async (selected) => {
    const name = selected.name.toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx")) {
      enqueueSnackbar("Upload a .csv or .xlsx file.", { variant: "error" });
      return;
    }

    setDialogOpen(false);
    setChecking(true);
    setResult(null);
    setFile(selected);
    setPage(0);
    setUploaded(false);
    setUploadedCount(0);

    try {
      const data = await postFile(selected, { dryRun: true });
      setResult(data);
      if (data.headerError) {
        enqueueSnackbar(data.headerError, { variant: "error" });
      }
    } catch (error) {
      setFile(null);
      enqueueSnackbar(errorMessage(error, "The file could not be checked."), {
        variant: "error",
      });
    } finally {
      setChecking(false);
    }
  };

  const applyFile = async () => {
    if (!file || !result?.ok) return;
    setApplying(true);
    let offset = 0;
    let applied = 0;
    const saveErrors = [];

    try {
      let hasMore = true;
      while (hasMore) {
        const data = await postFile(file, {
          dryRun: false,
          offset,
          limit: APPLY_BATCH_SIZE,
        });
        if (!data.ok) {
          setResult(data);
          enqueueSnackbar(
            data.headerError || "The file was not uploaded because a row did not match.",
            { variant: "error" },
          );
          return;
        }
        applied += data.applied || 0;
        saveErrors.push(...(data.applyErrors || []));
        const nextOffset = Number(data.nextOffset);
        if (!data.hasMore || !Number.isFinite(nextOffset) || nextOffset <= offset) {
          hasMore = false;
        } else {
          offset = nextOffset;
        }
      }

      if (saveErrors.length > 0) {
        enqueueSnackbar(
          `Saved ${applied} line${applied === 1 ? "" : "s"}. ${saveErrors.length} could not be saved.`,
          { variant: "warning" },
        );
        setResult((current) => ({
          ...(current || {}),
          applyErrors: saveErrors,
        }));
      } else {
        enqueueSnackbar(
          `Saved PTS dates for ${applied} line${applied === 1 ? "" : "s"}.`,
          { variant: "success" },
        );
        setUploaded(true);
        setUploadedCount(applied);
      }
    } catch (error) {
      enqueueSnackbar(errorMessage(error, "The upload did not finish."), {
        variant: "error",
      });
    } finally {
      setApplying(false);
    }
  };

  return (
    <AppShell>
      <Box sx={{ pl: 6, pr: 4, py: 2 }}>
        <Box sx={{ maxWidth: 1100, mx: "auto", mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            PTS Updates
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button variant="outlined" onClick={downloadPtsTemplate}>
              Download template
            </Button>
            <Button variant="contained" onClick={() => setDialogOpen(true)}>
              Upload PTS file
            </Button>
            {result && !result.headerError && !checking && (
              <Button
                variant="contained"
                disabled={!result.ok || uploaded || applying}
                onClick={applyFile}
                sx={{ ml: "auto" }}
              >
                Continue to upload
              </Button>
            )}
          </Box>
        </Box>

        {checking && <StatusSpinner label="Checking the file against purchase orders" />}
        {applying && <UploadLoadingScreen />}

        {!checking && !applying && result?.headerError && (
          <Alert severity="error">{result.headerError}</Alert>
        )}

        {!checking && !applying && result && !result.headerError && (
          <>
            <Alert severity={result.ok ? "success" : "error"} sx={{ mb: 2 }}>
              {uploaded
                ? `${uploadedCount} ${uploadedCount === 1 ? "row has" : "rows have"} uploaded successfully`
                : result.ok
                  ? `${result.summary.matched} rows match a PO line. Continue to upload the PTS dates.`
                  : `${result.summary.failed} of ${result.summary.total} rows need to be fixed before anything is saved.`}
            </Alert>
            {result.applyErrors?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {result.applyErrors.join(" ")}
              </Alert>
            )}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Row</TableCell>
                    <TableCell>PO Number</TableCell>
                    <TableCell>Master SKU</TableCell>
                    <TableCell>Units</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((row) => (
                    <TableRow key={row.row}>
                      <TableCell>
                        {row.matched ? (
                          <CheckCircleIcon color="success" aria-label="Matched" />
                        ) : (
                          <CancelIcon color="error" aria-label="Not matched" />
                        )}
                      </TableCell>
                      <TableCell>{row.row}</TableCell>
                      <TableCell>{row.poNumber}</TableCell>
                      <TableCell>{row.masterSku}</TableCell>
                      <TableCell>{row.units ?? ""}</TableCell>
                      <TableCell>{row.date ?? ""}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.message}</Typography>
                        {!row.matched && <RowDetails row={row} />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={result.rows.length}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload PTS file</DialogTitle>
        <DialogContent>
          <Box
            onDragOver={(event) => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOver(false);
              const dropped = event.dataTransfer.files?.[0];
              if (dropped) checkFile(dropped);
            }}
            sx={{
              border: "2px dashed",
              borderColor: dragOver ? "primary.main" : "grey.400",
              borderRadius: 1,
              p: 4,
              textAlign: "center",
              backgroundColor: dragOver ? "grey.100" : "transparent",
            }}
          >
            <Typography sx={{ mb: 2 }}>
              Drag and drop a CSV or Excel file here, or choose a file.
            </Typography>
            <Button variant="contained" component="label">
              Select file
              <input
                hidden
                type="file"
                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(event) => {
                  const selected = event.target.files?.[0];
                  event.target.value = "";
                  if (selected) checkFile(selected);
                }}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
};

const RowDetails = ({ row }) => {
  const fileRows = row.fileRows ?? [];
  const poLines = row.poLines ?? [];
  if (fileRows.length === 0 && poLines.length === 0) return null;

  return (
    <Box sx={{ mt: 0.5 }}>
      {fileRows.map((fileRow) => (
        <Typography key={fileRow.row} variant="caption" display="block">
          Also on row {fileRow.row}: {fileRow.units ?? "no"} units, date {fileRow.date ?? "blank"}.
        </Typography>
      ))}
      {poLines.map((line, index) => (
        <Typography key={`${line.quantity}-${index}`} variant="caption" display="block">
          PO line quantity {line.quantity ?? "blank"}, current PTS {line.pendingPtsActual ?? "none"}
          {line.inboundId ? `, inbound shipment ${line.inboundId}` : ""}.
        </Typography>
      ))}
    </Box>
  );
};

const LOADING_LINES = [
  "Your Purchase Orders are very important to us",
  "And your Inbound Shipments of course",
  "Hope you're having a lovely day",
];

const UploadLoadingScreen = () => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("reset");

  useEffect(() => {
    if (phase === "enter") {
      const timer = setTimeout(() => setPhase("hold"), 700);
      return () => clearTimeout(timer);
    }
    if (phase === "hold") {
      const timer = setTimeout(() => setPhase("leave"), 4800);
      return () => clearTimeout(timer);
    }
    if (phase === "leave") {
      const timer = setTimeout(() => {
        setIndex((current) => (current + 1) % LOADING_LINES.length);
        setPhase("reset");
      }, 700);
      return () => clearTimeout(timer);
    }
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase("enter"));
    });
    return () => cancelAnimationFrame(frame);
  }, [phase]);

  const offset = phase === "hold" || phase === "enter" ? "0" : phase === "leave" ? "-110%" : "110%";
  const visible = phase === "hold" || phase === "enter";

  return (
    <Box
      sx={{
        mt: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: "center" }}>
        Uploading to NetSuite, please don't refresh
      </Typography>
      <Box
        component="img"
        src={containersShip}
        alt="A crane loading containers from the dock onto a ship"
        sx={{ width: 420, maxWidth: "100%", height: "auto", display: "block" }}
      />
      <Box
        sx={{
          width: "100%",
          maxWidth: 1100,
          mx: "auto",
          overflow: "hidden",
          mt: 3,
          minHeight: 40,
          maskImage: "linear-gradient(to right, transparent, #000 14%, #000 86%, transparent)",
          WebkitMaskImage: "linear-gradient(to right, transparent, #000 14%, #000 86%, transparent)",
        }}
      >
        <Typography
          sx={{
            textAlign: "center",
            transform: `translateX(${offset})`,
            opacity: visible ? 1 : 0,
            transition: phase === "reset" ? "none" : "transform 0.7s ease, opacity 0.7s ease",
          }}
        >
          {LOADING_LINES[index]}
        </Typography>
      </Box>
    </Box>
  );
};

const StatusSpinner = ({ label }) => (
  <Box textAlign="center" mt={8}>
    <CircularProgress />
    <Typography sx={{ mt: 2 }}>{label}</Typography>
  </Box>
);

const postFile = async (file, { dryRun, offset = 0, limit }) => {
  const body = new FormData();
  body.append("file", file);
  const params = new URLSearchParams({ dryRun: dryRun ? "true" : "false" });
  if (!dryRun) {
    params.set("offset", String(offset));
    params.set("limit", String(limit));
  }
  const response = await axios.post(
    `${SLEDGEHAMMER_BASE_API}/dashboard/po/pts-upload?${params.toString()}`,
    body,
  );
  return response.data;
};

const errorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(" ");
  if (typeof message === "string" && message) return message;
  return fallback;
};

export default PtsUpdates;
